import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { GoogleMap } from "@jambushrushti/components/shared/GoogleMap";
import { FeedbackQRCode } from "@jambushrushti/components/shared/FeedbackQRCode";
import { Mail, MapPin, Phone, Send, Clock } from "lucide-react";

const contactInfo = [
  {
    icon: MapPin,
    title: "Address",
    content:
      "Muni Jambuvijayaji Gyan Mandir\nNear Adani Shantigram Jain Temple\nAdani Shantigram Township\nAhmedabad 382 421",
  },
  {
    icon: Mail,
    title: "Email",
    content: "contact@jambushrusti.org",
  },
  {
    icon: Phone,
    title: "Phone",
    content: "+91-98250 44911",
  },
  {
    icon: Clock,
    title: "Hours",
    content:
      "Mon - Sat: 10:00 AM - 6:00 PM IST\nVisiting: 10:45 AM - 5:15 PM IST",
  },
];

const CONTACT_API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
const CONTACT_ENDPOINT = `${CONTACT_API_BASE}/jambushrusti/cms/contact-us/`;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiryType: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID":
            import.meta.env.VITE_X_TENANTID ||
            "6cf833d7-bf6f-4688-9136-5674d8053b85",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          inquiry_type: formData.inquiryType || "general",
          subject: formData.subject.trim(),
          message: formData.message.trim(),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload.detail || payload.message || "Unable to send message."
        );
      }
      toast({
        title: "Message Sent",
        description: "Thank you for contacting us. We'll respond soon.",
      });
      setFormData({
        name: "",
        email: "",
        inquiryType: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast({
        title: "Message not sent",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="Contact Us"
        description="Get in touch with Muni Jambuvijayaji Research Center. Contact us for research inquiries, library access, or questions about Gurudev Muni Jambuvijayaji Maharaj Saheb's teachings."
      />
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-5 sm:py-8 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-heading animate-fade-up mb-3 text-3xl font-bold text-foreground sm:mb-6 sm:text-5xl">
              Contact Us
            </h1>
            <p className="font-body animate-fade-up text-base text-muted-foreground delay-100 sm:text-lg">
              Have questions about our research, library, or programs? We'd love
              to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-background py-6 sm:py-10 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-6xl gap-6 sm:gap-8 lg:grid-cols-3 lg:gap-12">
            {/* Contact Info */}
            <div className="space-y-4 sm:space-y-6">
              <h2 className="font-heading mb-3 text-xl font-bold text-foreground sm:mb-6 sm:text-2xl">
                Get in Touch
              </h2>
              {contactInfo.map((item, index) => (
                <div
                  key={item.title}
                  className="animate-fade-up flex items-start gap-4"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-medium text-foreground">
                      {item.title}
                    </h3>
                    <p className="font-body whitespace-pre-line text-muted-foreground">
                      {item.content}
                    </p>
                  </div>
                </div>
              ))}

              {/* Feedback QR Code */}
              <div className="pt-4">
                <FeedbackQRCode compact />
              </div>
            </div>

            {/* Contact Form */}
            <Card
              variant="elevated"
              className="animate-fade-up delay-200 lg:col-span-2"
            >
              <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-4">
                <CardTitle className="text-xl sm:text-2xl">
                  Send a Message
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-3.5 sm:space-y-6"
                >
                  <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="name" className="text-xs sm:text-sm">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Your name"
                        className="h-9 text-sm sm:h-10"
                      />
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="email" className="text-xs sm:text-sm">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="your@email.com"
                        className="h-9 text-sm sm:h-10"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                    <div className="space-y-1 sm:space-y-2">
                      <Label
                        htmlFor="inquiryType"
                        className="text-xs sm:text-sm"
                      >
                        Inquiry Type
                      </Label>
                      <Select
                        value={formData.inquiryType}
                        onValueChange={value =>
                          setFormData(prev => ({ ...prev, inquiryType: value }))
                        }
                      >
                        <SelectTrigger className="h-9 text-sm sm:h-10">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">
                            General Inquiry
                          </SelectItem>
                          <SelectItem value="library">Library Visit</SelectItem>
                          <SelectItem value="research">
                            Research Collaboration
                          </SelectItem>
                          <SelectItem value="technical">
                            Technical Support
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="subject" className="text-xs sm:text-sm">
                        Subject *
                      </Label>
                      <Input
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            subject: e.target.value,
                          }))
                        }
                        placeholder="Subject of your message"
                        className="h-9 text-sm sm:h-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="message" className="text-xs sm:text-sm">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      required
                      rows={3.5 as any}
                      value={formData.message}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      placeholder="How can we help you?"
                      className="min-h-[90px] text-sm"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-secondary/20 py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mb-10 text-center">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground lg:text-3xl">
              Find Us
            </h2>
            <p className="mx-auto max-w-xl text-muted-foreground">
              Visit our library and research center located near Adani
              Shantigram Jain Temple
            </p>
          </div>
          <div className="mx-auto max-w-5xl">
            <GoogleMap height="450px" />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
